// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import Calendar from "@insite/mobius/Icons/Calendar";
import Copy from "@insite/mobius/Icons/Copy";
import Edit from "@insite/mobius/Icons/Edit";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Print from "@insite/mobius/Icons/Printer";
import Export from "@insite/mobius/Icons/Share";
import Share from "@insite/mobius/Icons/Share";
import { LinkProps } from "@insite/mobius/Link";
import { actionStyles } from "@tps/Overrides/Widgets/MyListsDetails/MyListsDetailsActions";
import IconMoreInactive from "@tps/XDL/Icons/IconMoreInactive";
import Trash from "@tps/XDL/Icons/IconTrashHover";
import { color, createLinkIconStyles, ellipsis, generateButtonLinkStyle, xScreen } from "@tps/XDL/Utilities";
import { css } from "styled-components";

export const createListDetailButtonStyle = (iconSrc: React.ComponentType | string) => {
    return {
        variant: "secondary",
        icon: {
            src: iconSrc,
            position: "right",
        },
        css: css`
            padding: 0 15px 0 0;
            border-color: transparent;
            ${IconWrapper} {
                margin-bottom: 5px;
                color: ${color("primary")};
            }
        `,
    } as LinkProps;
};

actionStyles.wrapper = {
    css: css`
        display: flex;
        justify-content: space-between;
        padding: 15px;
        margin-bottom: 8px;
        border: 1px solid ${color("common.border")};
        ${xScreen("lte", "md")} {
            flex-direction: column;
        }
    `,
};
actionStyles.buttonWrapper = {
    css: css`
        display: flex;
        flex-direction: row-reverse;
        justify-content: flex-start;
        gap: 15px;
        ${xScreen("lte", "md")} {
            margin-top: 20px;
            justify-content: space-between;
        }
        ${xScreen("lte", "sm")} {
            margin-top: 20px;
            flex-direction: column-reverse;
            align-items: flex-start;
        }
    `,
};

actionStyles.listInfoWrapper = {
    css: css`
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        gap: 10px;
        ${xScreen("lte", "md")} {
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
        }
    `,
};

actionStyles.innerButtonWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 10px;
        ${xScreen("lte", "sm")} {
            flex-direction: row;
        }
    `,
};
actionStyles.wideHidden = {
    below: "lg",
    css: css`
        display: flex;
        flex-wrap: nowrap;
        margin-bottom: 12px;
    `,
};
actionStyles.printButton = {
    ...createLinkIconStyles(Print, {
        typographyProps: {
            css: css`
                padding: 0 15px 0 0;
            `,
        },
    }),
};
actionStyles.addTagsButton = {
    ...createLinkIconStyles(IconMoreInactive, {
        typographyProps: {
            css: css`
                padding: 0 15px 0 0;
            `,
        },
    }),
};

actionStyles.hideButton = {
    ...createLinkIconStyles(EyeOff, {
        typographyProps: {
            css: css`
                padding: 0 15px 0 0;
            `,
        },
    }),
};

actionStyles.copyButton = {
    ...createLinkIconStyles(Copy, {
        typographyProps: {
            css: css`
                padding: 0 15px 0 0;
            `,
        },
    }),
};
actionStyles.exportButton = {
    ...createLinkIconStyles(Export, {
        typographyProps: {
            css: css`
                padding: 0 15px 0 0;
            `,
        },
    }),
};
actionStyles.scheduleButton = {
    ...createLinkIconStyles(Calendar, {
        typographyProps: {
            css: css`
                padding: 0 15px 0 0;
            `,
        },
    }),
};
actionStyles.editButton = {
    ...createLinkIconStyles(Edit, {
        typographyProps: {
            css: css`
                padding: 0 15px 0 0;
            `,
        },
    }),
};
actionStyles.shareButton = {
    ...createLinkIconStyles(Share, {
        typographyProps: {
            css: css`
                padding: 0 15px 0 0;
            `,
        },
    }),
};
actionStyles.deleteButton = {
    ...createLinkIconStyles(Trash, {
        typographyProps: {
            css: css`
                padding: 0 15px 0 0;
            `,
        },
    }),
};
actionStyles.addItemButton = {
    typographyProps: {
        weight: "bold",
        css: css`
            white-space: nowrap;
        `,
    },
    css: css`
        width: 100%;
        padding: 0 15px;
        margin-right: 10px;
    `,
};
actionStyles.removeSelectedButton = {
    typographyProps: {
        weight: "bold",
        css: css`
            white-space: nowrap;
        `,
    },
    css: css`
        width: 100%;
        padding: 0 15px;
    `,
    variant: "secondary",
};
actionStyles.addListButton = {
    css: css`
        width: 100%;
        padding: 0 15px;
        white-space: nowrap;
    `,
};
actionStyles.editListModal = {
    sizeVariant: "small",
};
actionStyles.copyListModal = {
    sizeVariant: "small",
};
actionStyles.overflowMenu = {
    cssOverrides: {
        wrapper: css`
            order: 1;
            margin-left: 5px;
            padding-top: 5px;
        `,
    },
};

actionStyles.headerWrapper = {
    css: css`
        max-width: 50%;
        ${xScreen("lte", "sm")} {
            max-width: none;
        }
    `,
};

actionStyles.listActionsWrapper = {
    css: css`
        margin-top: 10px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    `,
};

actionStyles.nameStatusWrapper = {
    css: css`
        display: flex;
        align-items: flex-end;
        ${xScreen("lte", "md")} {
            display: block;
        }
    `,
};

actionStyles.nameText = {
    size: 30,
    variant: "h1",
    color: "primary",
    css: css`
        display: block;
        margin-bottom: 5px;
        max-width: calc(100% - 100px);
        ${xScreen("lte", "md")} {
            max-width: 100%;
        }
    `,
};

actionStyles.sharingStatus = {
    statusText: {
        css: css`
            margin-left: 10px;
            margin-bottom: 10px;
            ${xScreen("lte", "md")} {
                margin-left: 0;
            }
        `,
    },
};

actionStyles.lastUpdateText = {
    as: "p",
    size: 14,
    italic: true,
    weight: "bold",
    color: "common.ui5",
    css: css`
        margin-bottom: 10px;
    `,
};

actionStyles.totalWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        margin-right: 20px;
        gap: 5px;
        ${xScreen("lte", "sm")} {
            flex-direction: row;
        }
    `,
};

actionStyles.totalLabel = {
    as: "p",
    size: 22,
    weight: "bold",
    css: css`
        ${xScreen("lte", "sm")} {
            font-size: 18px;
        }
    `,
};

actionStyles.totalText = {
    as: "p",
    size: 22,
    weight: "bold",
    css: css`
        ${xScreen("lte", "sm")} {
            font-size: 18px;
        }
    `,
};

actionStyles.totalVatLabelText = {
    size: 12,
    css: css`
        margin-left: 5px;
    `,
};

actionStyles.totalWithoutVatText = {
    weight: "bold",
    css: css`
        margin-left: 5px;
    `,
};

actionStyles.totalWithoutVatLabelText = {
    size: 12,
    css: css`
        margin-left: 5px;
    `,
};

actionStyles.wishlistCountWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        margin-right: 20px;
    `,
};

actionStyles.wishlistLabel = {
    size: 22,
    weight: "bold",
    css: css`
        ${xScreen("lte", "sm")} {
            font-size: 18px;
        }
    `,
};

actionStyles.wishlistCount = {
    size: 22,
    weight: "bold",
    css: css`
        ${xScreen("lte", "sm")} {
            font-size: 18px;
        }
    `,
};

actionStyles.deleteListModal = {
    submitButton: {
        color: "primary",
    },
    buttonsGridItem: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
};
